/**
 * Auth slice — Redux Toolkit state + thunks for the current user session.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   user: AuthUser | null,
 *   isAuthenticated: boolean,
 *   permissions: Permission[],
 *   roles: UserRole[],
 *   expiresAt: number | null,   // epoch ms; mirrors tokenStore for UI use
 *   status: 'idle' | 'loading' | 'error',
 *   error: string | null,
 * }
 * ```
 *
 * Invariants
 * ----------
 * - Tokens never live on the Redux tree — they belong to `tokenStore`.
 *   Only `expiresAt` is mirrored here because the countdown UI reads it
 *   through `useAppSelector` and we do not want every tick of a timer to
 *   force a tokenStore read from React.
 * - `hydrateFromStorage` restores the cached user profile for optimistic
 *   UI, but does NOT flip `isAuthenticated` to true. Authentication can
 *   only be claimed after a fresh login OR a successful heartbeat — the
 *   cached profile alone proves nothing about the current session.
 * - `logout` clears the slice AND calls `tokenStore.clearTokens()` AND
 *   removes the cached user from `localStorage`. A single action
 *   encapsulates "sign out everywhere" so callers never have to remember
 *   the three cleanup paths.
 *
 * SH-AUTH-005.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import * as authApi from '@dfq/shared/api/auth';
import { AuthApiError } from '@dfq/shared/api/auth';
import { clearTokens } from '@dfq/shared/auth/tokenStore';
import type {
  AuthorizationPayload,
  AuthUser,
  LoginRequest,
  Permission,
  RegisterRequest,
  RegisterResult,
  UserRole,
} from '@dfq/shared/types/auth';
import { isRestorePending } from '@dfq/shared/types/auth';

/**
 * Structured rejection payload for the `login` thunk. Consumers read
 * `code` (when present) to render a specific UI treatment
 * (invalid-credentials, suspended-account, etc.) and fall back to
 * `message` otherwise. HTTP status is included so callers can map raw
 * 401/403/422 responses without re-inspecting the axios error.
 *
 * Backwards compatibility: the reducer reads `payload.message` when the
 * payload is a structured object and falls through to the payload string
 * for the older test-shape `login.rejected(err, id, arg, 'string')`.
 */
export interface LoginRejection {
  message: string;
  code?: string;
  status?: number;
}

/**
 * Structured rejection payload for the `register` thunk. Shares the shape
 * with `LoginRejection` but also exposes the server-provided `fieldErrors`
 * map so the view can call `form.setError(field, ...)` on each entry.
 */
export interface RegisterRejection {
  message: string;
  code?: string;
  status?: number;
  fieldErrors?: Record<string, string[]>;
}

type AxiosLikeError = {
  isAxiosError: true;
  response?: {
    status?: number;
    data?: {
      message?: string;
      error_code?: string;
      errors?: Record<string, string[]>;
      error?: { code?: string };
    };
  };
  message: string;
};

function isAxiosLikeError(err: unknown): err is AxiosLikeError {
  return typeof err === 'object' && err !== null && (err as { isAxiosError?: boolean }).isAxiosError === true;
}

const USER_STORAGE_KEY = 'dfq-user';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------
export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  permissions: Permission[];
  roles: UserRole[];
  /** Epoch-ms timestamp of token expiry. Null when signed out. */
  expiresAt: number | null;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  /**
   * When `true`, the user profile was restored from `localStorage` and the
   * app should still run a `heartbeat` / `me()` to confirm the session is
   * live before claiming authentication.
   */
  hydratedFromStorage: boolean;
  /**
   * MFA challenge token — populated when `login` returns `mfa_required`.
   * Consumed by the MFA verification screen (SF-AUTH-006 / CON-AUTH-006).
   */
  mfaChallengeToken: string | null;
  /**
   * Boot phase gate for the silent-refresh path (SF-AUTH-PERSIST).
   *
   * `pending` — bootstrap is in flight (cookie refresh not yet settled).
   * `ready`   — bootstrap has resolved; route guards may act.
   *
   * `RequireAuth` must not redirect to /login while `pending` — the user
   * might still be restored via a successful cookie refresh.
   *
   * Optional so preloaded-state literals (tests, SSR seeds) may omit it;
   * the slice always initialises it to `ready` and consumers treat an
   * absent value as `ready` (only an explicit `pending` gates the guard).
   */
  bootPhase?: 'pending' | 'ready';
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  permissions: [],
  roles: [],
  expiresAt: null,
  status: 'idle',
  error: null,
  hydratedFromStorage: false,
  mfaChallengeToken: null,
  bootPhase: 'ready',
};

// ----------------------------------------------------------------------------
// Persistence helpers. Tokens are never persisted — only the user profile.
// ----------------------------------------------------------------------------
function persistUser(user: AuthUser | null): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }
  try {
    if (user === null) {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    } else {
      window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    }
  } catch {
    // localStorage can throw in private-browsing / quota-exceeded modes.
    // User persistence is best-effort; a throw here must not break login.
  }
}

function readPersistedUser(): AuthUser | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as AuthUser;
    // Minimal shape guard — anything not clearly a user record is
    // treated as corrupt and cleared.
    if (typeof parsed.id !== 'number' || typeof parsed.email !== 'string') {
      window.localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function expiresAtFromAuthorization(auth: AuthorizationPayload): number {
  return Date.now() + auth.expires_in * 1000;
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------
export const login = createAsyncThunk('auth/login', async (credentials: LoginRequest, { rejectWithValue }) => {
  try {
    const result = await authApi.login(credentials);
    return result;
  } catch (err) {
    // Structured rejection so the UI can branch on `code` / `status`
    // without re-inspecting the raw error inside a view.
    if (err instanceof AuthApiError) {
      return rejectWithValue({
        message: err.message,
        code: err.code,
      } satisfies LoginRejection);
    }
    if (isAxiosLikeError(err)) {
      return rejectWithValue({
        message: err.message,
        status: err.response?.status,
        code: err.response?.data?.error_code ?? err.response?.data?.error?.code,
      } satisfies LoginRejection);
    }
    return rejectWithValue({
      message: err instanceof Error ? err.message : 'Login failed',
    } satisfies LoginRejection);
  }
});

/**
 * `verifyMfa` thunk — completes an MFA sign-in challenge with a TOTP or
 * recovery code. Rejects with the envelope's `message` and `fieldErrors`
 * (`code`, `challenge_token`) so the view can show what the server said.
 */
export const verifyMfa = createAsyncThunk(
  'auth/verifyMfa',
  async (body: authApi.VerifyMfaChallengeRequest, { rejectWithValue }) => {
    try {
      return await authApi.verifyMfaChallenge(body);
    } catch (err) {
      return rejectWithValue(toRegisterRejection(err, 'Verification failed'));
    }
  },
);

/**
 * `register` thunk — calls `authApi.register`, receives the authorization
 * envelope, side-effects the tokens into the in-memory store, and returns
 * the `{ user, authorization }` tuple for the reducer. On failure, rejects
 * with a structured `RegisterRejection` payload so the view can branch on
 * `code` (e.g. `EMAIL_ALREADY_REGISTERED`) or map `fieldErrors` back onto
 * the form via `form.setError`.
 *
 * SF-AUTH-002.
 */
export const register = createAsyncThunk('auth/register', async (body: RegisterRequest, { rejectWithValue }) => {
  try {
    return await authApi.register(body);
  } catch (err) {
    return rejectWithValue(toRegisterRejection(err, 'Registration failed'));
  }
});

/**
 * `restoreAccount` thunk — redeems an emailed restore link for a deleted
 * customer with the password they choose now, and signs them in, same as a
 * fresh `register` (#1828).
 */
export const restoreAccount = createAsyncThunk(
  'auth/restoreAccount',
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      return await authApi.restoreAccount(token, password);
    } catch (err) {
      return rejectWithValue(toRegisterRejection(err, 'That restore link is no longer valid.'));
    }
  },
);

function toRegisterRejection(err: unknown, fallback: string): RegisterRejection {
    if (err instanceof AuthApiError) {
      return {
        message: err.message,
        code: err.code,
        fieldErrors: err.fieldErrors,
      };
    }
    if (isAxiosLikeError(err)) {
      const data = err.response?.data;
      return {
        message: data?.message ?? err.message,
        status: err.response?.status,
        code: data?.error_code ?? data?.error?.code,
        fieldErrors: data?.errors,
      };
    }
  return {
    message: err instanceof Error ? err.message : fallback,
  };
}

export const logout = createAsyncThunk('auth/logout', async () => {
  // Best-effort server logout. Local cleanup runs in the reducer regardless.
  try {
    await authApi.logout();
  } catch {
    // swallow — the reducer still runs and local state is cleaned
  }
});

export const heartbeat = createAsyncThunk('auth/heartbeat', async (_, { rejectWithValue }) => {
  try {
    const result = await authApi.heartbeat();
    return result.authorization;
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err.message : 'Heartbeat failed');
  }
});

function applySignedIn(state: AuthState, payload: RegisterResult): void {
  state.user = payload.user;
  state.roles = payload.user.roles;
  state.permissions = payload.user.permissions;
  state.isAuthenticated = true;
  state.expiresAt = expiresAtFromAuthorization(payload.authorization);
  state.status = 'idle';
  state.error = null;
  state.hydratedFromStorage = false;
  state.mfaChallengeToken = null;
  persistUser(payload.user);
}

function applyRegisterRejected(state: AuthState, payload: unknown, fallback: string): void {
  state.status = 'error';
  const rejection = payload as RegisterRejection | string | undefined;
  if (typeof rejection === 'string') {
    state.error = rejection;
  } else if (rejection && typeof rejection === 'object' && 'message' in rejection) {
    state.error = rejection.message;
  } else {
    state.error = fallback;
  }
  state.user = null;
  state.isAuthenticated = false;
}

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Restore the cached user profile from `localStorage`. Does NOT flip
     * `isAuthenticated` — the app must still confirm the session via a
     * heartbeat / fresh login.
     */
    hydrateFromStorage(state) {
      const cached = readPersistedUser();
      if (cached !== null) {
        state.user = cached;
        state.roles = cached.roles;
        state.permissions = cached.permissions;
        state.hydratedFromStorage = true;
      }
    },

    /**
     * Dispatched by the axios response interceptor when refresh fails.
     * Wipes session-bearing fields without invoking the logout thunk
     * (which would re-hit the server). Aliased to `auth/tokensExpired`
     * — the exact string the interceptor dispatches.
     */
    tokensExpired(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.permissions = [];
      state.roles = [];
      state.expiresAt = null;
      state.mfaChallengeToken = null;
      state.error = 'Session expired. Please sign in again.';
      persistUser(null);
    },

    /**
     * Record a successful heartbeat or manual token rotation. Keeps the
     * user block untouched and only refreshes the expiry mirror. The
     * token store is updated by the API layer before this reducer runs.
     */
    heartbeatSuccess(state, action: PayloadAction<AuthorizationPayload>) {
      state.expiresAt = expiresAtFromAuthorization(action.payload);
      state.isAuthenticated = true;
      state.status = 'idle';
      state.error = null;
    },

    /**
     * Store a profile fetched from the server rather than restored from
     * storage. A session established server-side (PL-INFRA-020 login link)
     * has no cached profile, and the route guards read the role off it.
     */
    profileLoaded(state, action: PayloadAction<AuthUser>) {
      // Mirrors the login success path exactly: the route guards read
      // `state.roles`, not `state.user.roles`, so setting only the user left
      // an authenticated admin looking role-less and bounced to /403.
      state.user = action.payload;
      state.roles = action.payload.roles;
      state.permissions = action.payload.permissions;
      state.isAuthenticated = true;
      state.status = 'idle';
      state.error = null;
      persistUser(action.payload);
    },

    /**
     * Clear any transient error on the slice — called by the UI after a
     * user dismisses a toast or navigates away.
     */
    clearError(state) {
      state.error = null;
    },

    /**
     * Mark boot phase as pending — dispatched by `bootstrapAuth` when a
     * silent cookie refresh is about to be attempted so `RequireAuth` knows
     * not to redirect yet (SF-AUTH-PERSIST).
     */
    bootPhasePending(state) {
      state.bootPhase = 'pending';
    },

    /**
     * Mark boot phase as ready — dispatched by `bootstrapAuth` once the
     * cookie refresh attempt (success or failure) has settled. Route guards
     * may proceed to evaluate `isAuthenticated` only after this point.
     */
    bootPhaseReady(state) {
      state.bootPhase = 'ready';
    },

    /**
     * Mirror an MFA enrollment status change onto the in-memory user
     * profile. Dispatched by the customer Security page (SF-ACCT-009)
     * after `confirmMfa.fulfilled` and `disableMfa.fulfilled` so flows
     * that gate on `state.auth.user.mfaEnabled` read a fresh value
     * without forcing a re-login or `/auth/me` round-trip.
     */
    clearMfaChallenge(state) {
      state.mfaChallengeToken = null;
    },
    setMfaEnabled(state, action: PayloadAction<boolean>) {
      if (state.user) {
        state.user = { ...state.user, mfaEnabled: action.payload };
        persistUser(state.user);
      }
    },

    /**
     * Optimistically merge updated profile fields into the in-memory
     * `auth.user` object after a successful `updateProfile` save
     * (SF-ACCT-008). The persisted `dfq-user` localStorage entry is
     * refreshed so a page reload reflects the new name immediately.
     *
     * Only the fields the customer can edit on the profile page are
     * accepted here — `roles`, `permissions`, `id`, etc. stay untouched
     * because the profile save endpoint does not surface them.
     */
    updateUserProfile(
      state,
      action: PayloadAction<{
        firstName: string;
        lastName: string;
        email: string;
        companyName?: string | null;
        phone?: string | null;
      }>,
    ) {
      if (!state.user) return;
      state.user = {
        ...state.user,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        companyName:
          action.payload.companyName ?? state.user.companyName ?? null,
        phone: action.payload.phone ?? state.user.phone ?? null,
      };
      persistUser(state.user);
    },
  },
  extraReducers(builder) {
    builder
      // -- login ---------------------------------------------------------
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.mfaChallengeToken = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const payload = action.payload;
        if (payload.mfaRequired) {
          state.status = 'idle';
          state.mfaChallengeToken = payload.challengeToken;
          return;
        }
        state.user = payload.user;
        state.roles = payload.user.roles;
        state.permissions = payload.user.permissions;
        state.isAuthenticated = true;
        state.expiresAt = expiresAtFromAuthorization(payload.authorization);
        state.status = 'idle';
        state.error = null;
        state.hydratedFromStorage = false;
        persistUser(payload.user);
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'error';
        // Structured payload (LoginRejection) is the new default, but we
        // still accept the legacy string shape used by older tests /
        // callers. Either way we collapse to a human-readable message
        // for `state.error`.
        const payload = action.payload as LoginRejection | string | undefined;
        if (typeof payload === 'string') {
          state.error = payload;
        } else if (payload && typeof payload === 'object' && 'message' in payload) {
          state.error = payload.message;
        } else {
          state.error = 'Login failed';
        }
        state.user = null;
        state.isAuthenticated = false;
      })
      // -- verifyMfa -----------------------------------------------------
      .addCase(verifyMfa.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyMfa.fulfilled, (state, action) => {
        applySignedIn(state, action.payload);
      })
      .addCase(verifyMfa.rejected, (state, action) => {
        state.status = 'error';
        state.error = (action.payload as RegisterRejection | undefined)?.message ?? 'Verification failed';
      })
      // -- register ------------------------------------------------------
      .addCase(register.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        if (isRestorePending(action.payload)) {
          state.status = 'idle';
          state.error = null;
          return;
        }
        applySignedIn(state, action.payload);
      })
      .addCase(register.rejected, (state, action) => {
        applyRegisterRejected(state, action.payload, 'Registration failed');
      })
      // -- restoreAccount ------------------------------------------------
      .addCase(restoreAccount.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(restoreAccount.fulfilled, (state, action) => {
        applySignedIn(state, action.payload);
      })
      .addCase(restoreAccount.rejected, (state, action) => {
        applyRegisterRejected(state, action.payload, 'That restore link is no longer valid.');
      })
      // -- heartbeat -----------------------------------------------------
      .addCase(heartbeat.fulfilled, (state, action) => {
        state.expiresAt = expiresAtFromAuthorization(action.payload);
        state.isAuthenticated = true;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(heartbeat.rejected, (state, action) => {
        state.error = (action.payload as string) ?? 'Heartbeat failed';
      })
      // -- logout --------------------------------------------------------
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.roles = [];
        state.expiresAt = null;
        state.mfaChallengeToken = null;
        state.status = 'idle';
        state.error = null;
        state.hydratedFromStorage = false;
        clearTokens();
        persistUser(null);
      })
      .addCase(logout.rejected, (state) => {
        // Local cleanup even on server failure — user's intent was to leave.
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.roles = [];
        state.expiresAt = null;
        state.mfaChallengeToken = null;
        clearTokens();
        persistUser(null);
      });
  },
});

export const {
  hydrateFromStorage,
  profileLoaded,
  tokensExpired,
  heartbeatSuccess,
  clearError,
  setMfaEnabled,
  clearMfaChallenge,
  updateUserProfile,
  bootPhasePending,
  bootPhaseReady,
} = authSlice.actions;

export { authSlice };
export const authReducer = authSlice.reducer;
export default authReducer;

/**
 * Exposed for unit tests that want to assert the storage key without
 * duplicating the constant.
 */
export const USER_STORAGE_KEY_FOR_TESTS = USER_STORAGE_KEY;
